import React, { useRef, useEffect } from 'react';
import { RouteComponentProps, useLocation, useRouteMatch } from 'react-router';
import PageContainer from '../PageContainer';
import PaddedContent from '../../components/housing/PaddedContent';
import SmallLiftup from '../../components/liftup/SmallLiftup';
import Grid from '../../components/common/Grid';
import { Body2, LinkButton } from '../../components/common';
import { H1 } from '../../components/basic/Text';
import { useMessageGetter } from 'react-message-context';
import styled from 'styled-components/macro';
import Pagination from '../../components/common/Pagination';
import { useKennoTenant } from '../../context/kennoTenantContext';
import { loadPublicEvents } from '../../context/kennoTenantActions';
import { formatDateTimeFromString } from '../../utils';
import { EVENT_PAGE_QUERY_PARAM, getBackgroundForEventType, getPictogramForEventType } from './event-utils';

const ITEMS_PER_PAGE = 12;

const Title = styled(H1)`
  margin-bottom: 40px;
`;

const BuildingEventsPage = ({ history }: RouteComponentProps) => {
  const t = useMessageGetter('myBuilding.events');
  const tCommon = useMessageGetter('common');
  const ref = useRef<HTMLElement | null>(null);
  const { url: currentUrl } = useRouteMatch();
  const query = new URLSearchParams(useLocation().search);
  const [{ publicEvents }, dispatch] = useKennoTenant();
  const eventsToShow = publicEvents.data || [];

  useEffect(() => {
    loadPublicEvents(dispatch);
  }, [dispatch]);

  const numberOfPages = Math.ceil(eventsToShow.length / ITEMS_PER_PAGE);

  const pageParam = parseInt(query.get(EVENT_PAGE_QUERY_PARAM) || '', 10) || 1;
  // Ensure query param is between 1 and total number of pages, default to 1 if value is invalid.
  const currentPage = pageParam < 1 || pageParam > numberOfPages ? 1 : pageParam;
  const currentPageEvents = eventsToShow.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > numberOfPages || newPage === currentPage) {
      return;
    }
    // Keep page in query params
    query.set(EVENT_PAGE_QUERY_PARAM, newPage.toString());
    history.push({ search: query.toString() });

    // Using setTimeout fixes the scroll on certain mobile views
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  };

  return (
    <PageContainer history={history}>
      <PaddedContent>
        <section ref={ref}>
          <Title>{t('title')}</Title>
          <Grid>
            {currentPageEvents.length === 0 && <Body2>{t('noData')}</Body2>}
            {currentPageEvents.map((event) => (
              <SmallLiftup
                key={event.id}
                title={event.formData.name}
                subTitle={formatDateTimeFromString(event.formData.date, false, event.formData.time)}
                pictogram={getPictogramForEventType(event.formData.eventType)}
                mainButton={
                  <LinkButton to={`${currentUrl}/${event.id}?${EVENT_PAGE_QUERY_PARAM}=${currentPage}`} primary>
                    {tCommon('readMore')}
                  </LinkButton>
                }
                background={getBackgroundForEventType(event.formData.eventType)}
              />
            ))}
          </Grid>
          {numberOfPages > 1 && (
            <Pagination handlePageChange={handlePageChange} numberOfPages={numberOfPages} currentPage={currentPage} />
          )}
        </section>
      </PaddedContent>
    </PageContainer>
  );
};

export default BuildingEventsPage;
